// Fixed InspectionComponents.jsx with proper configuration integration

import React, { useState, useEffect, useCallback } from 'react';
import { LoadingSpinner } from './ui/LoadingSpinner.jsx';
import { useAppContext } from '../context/AppContext.jsx';
import { api } from '../api/reportingApi.js';

// ==================== IMAGE COMPONENTS ====================

export const ImageWithFallback = ({ src, alt, style, className, fallbackMessage = "Image not available", onClick }) => {
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        setHasError(false);
    }, [src]);

    const clickableClass = onClick ? 'cursor-pointer' : '';

    if (hasError || !src) {
        return (
            <div className={`d-flex align-items-center justify-content-center border rounded bg-light ${className}`} style={style}>
                <small className="text-muted">{fallbackMessage}</small>
            </div>
        );
    }

    return (
        <img
            src={src}
            alt={alt}
            style={style}
            className={`${className} ${clickableClass}`}
            onError={() => setHasError(true)}
            onClick={onClick}
        />
    );
};

export const ImageModal = ({ src, alt, show, onClose }) => {
    if (!show) return null;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }} onClick={onClose}>
            <div className="modal-dialog modal-xl modal-dialog-centered">
                <div className="modal-content bg-transparent border-0">
                    <div className="modal-body text-center">
                        <img src={src} alt={alt} className="img-fluid" style={{ maxHeight: '90vh' }} />
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==================== GROUND TRUTH CONTROLS ====================

export const GroundTruthControls = ({ inspection, onSetGroundTruth, isLoading }) => {
    const [isEditing, setIsEditing] = useState(!inspection.confirmation_status);

    useEffect(() => {
        setIsEditing(!inspection.confirmation_status);
    }, [inspection.confirmation_status]);

    const getConfirmationIcon = (status) => {
        switch (status) {
            case 'normal': return 'fa-check-circle';
            case 'defective': return 'fa-times-circle';
            case 'unsure': return 'fa-question-circle';
            default: return 'fa-info-circle';
        }
    };

    const handleSetTruth = (status) => {
        onSetGroundTruth(inspection._id, status);
        setIsEditing(false);
    };

    if (!isEditing && inspection.confirmation_status) {
        return (
            <div className="text-center">
                <div>
                    <span className={`badge bg-${
                        inspection.confirmation_status === 'normal' ? 'success' :
                        inspection.confirmation_status === 'defective' ? 'danger' : 'warning'
                    }`}>
                        <i className={`fas ${getConfirmationIcon(inspection.confirmation_status)} me-1`}></i>
                        {inspection.confirmation_status}
                    </span>
                    <small className="text-muted d-block mt-1">by {inspection.confirmed_by || 'user'}</small>
                </div>
                <button
                    className="btn btn-sm btn-link text-muted mt-1"
                    onClick={() => setIsEditing(true)}
                    disabled={isLoading}
                >
                    Change
                </button>
            </div>
        );
    }

    return (
        <div className="btn-group" role="group">
            <button
                className="btn btn-sm btn-outline-success"
                onClick={() => handleSetTruth('normal')}
                title="Mark as Normal"
                disabled={isLoading}
            >
                <i className="fas fa-check"></i>
            </button>
            <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => handleSetTruth('defective')}
                title="Mark as Defective"
                disabled={isLoading}
            >
                <i className="fas fa-times"></i>
            </button>
            <button
                className="btn btn-sm btn-outline-warning"
                onClick={() => handleSetTruth('unsure')}
                title="Mark as Unsure"
                disabled={isLoading}
            >
                <i className="fas fa-question"></i>
            </button>
        </div>
    );
};

// ==================== MODEL VISUALIZATION COMPONENTS ====================

export const ModelVisualizationCard = ({ displayName, modelData, onImageClick, showConfidence = true }) => {
    const getVisualizationUrl = (data) => {
        return data.heatmap_url ||
               data.localization_image_url ||
               data.localization_url ||
               data.visualization ||
               data.heatmap_data ||
               data.segmentation_url ||
               data.overlay_url ||
               data.annotation_url;
    };

    const getVisualizationType = (data) => {
        if (data.heatmap_url || data.heatmap_data) return "Heatmap";
        if (data.localization_image_url || data.localization_url || data.localization) return "Localization";
        if (data.segmentation_url) return "Segmentation";
        if (data.overlay_url) return "Overlay";
        if (data.annotation_url) return "Annotation";
        if (data.visualization) return "Visualization";
        return "Analysis";
    };

    const getNumber = (field) => {
        if (field === null || field === undefined) return null;
        if (typeof field === 'number') return field;
        if (typeof field === 'object') return field.$numberDouble ?? field.$numberInt ?? field.$numberLong ?? null;
        return null;
    };

    const visualizationUrl = getVisualizationUrl(modelData);
    const visualizationType = getVisualizationType(modelData);
    const confidence = getNumber(modelData.confidence);
    const classification = getNumber(modelData.class);
    const numDefects = getNumber(modelData.num_defects);

    return (
        <div className="col-md-6 col-lg-4 mb-3">
            <div className="card h-100">
                <div className="card-header">
                    <h6 className="mb-0">{displayName}</h6>
                    <small className="text-muted">{visualizationType}</small>
                </div>
                <div className="card-body">
                    {/* Model Results with conditional confidence display */}
                    <div className="mb-3">
                        <p className="mb-1">
                            <strong>Classification:</strong> {
                                classification === 1 ? 'Defective' :
                                classification === 0 ? 'Normal' : 'Unknown'
                            }
                        </p>
                        {/* CONDITIONAL: Only show confidence if enabled in configuration */}
                        {showConfidence && (
                            <p className="mb-1">
                                <strong>Confidence:</strong> {
                                    confidence !== null ? `${(confidence * 100).toFixed(1)}%` : 'N/A'
                                }
                            </p>
                        )}
                        {modelData.defect_type && (
                            <p className="mb-1">
                                <strong>Defect Type:</strong>
                                <span className="badge bg-secondary ms-1">{modelData.defect_type}</span>
                            </p>
                        )}
                        {numDefects !== null && numDefects > 0 && (
                            <p className="mb-1"><strong>Defects Found:</strong> {numDefects}</p>
                        )}
                        {modelData.score !== undefined && (
                            <p className="mb-1"><strong>Score:</strong> {getNumber(modelData.score)}</p>
                        )}
                    </div>

                    {/* Visualization */}
                    {visualizationUrl && (
                        <div className="text-center">
                            <small className="text-muted d-block mb-2">{visualizationType} Visualization</small>
                            <ImageWithFallback
                                src={visualizationUrl}
                                alt={`${displayName} ${visualizationType}`}
                                className="img-fluid rounded border"
                                style={{ maxHeight: '200px', cursor: 'pointer' }}
                                fallbackMessage={`${visualizationType} not available`}
                                onClick={() => onImageClick(visualizationUrl)}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ==================== INSPECTION UTILITIES ====================

export const getAllDefectTypes = (inspection) => {
    const { aggregate_result, model_results } = inspection;
    if (!aggregate_result || aggregate_result.classification !== 'defective') {
        return [];
    }

    const defectSet = new Set();
    const individualModels = model_results?.models || model_results;

    // Gather defects from all individual models
    if (individualModels && typeof individualModels === 'object') {
        Object.values(individualModels).forEach(modelData => {
            // Handle single 'defect_type' property
            if (modelData.defect_type) {
                defectSet.add(modelData.defect_type);
            }
            // Handle 'detected_defects' array
            if (Array.isArray(modelData.detected_defects)) {
                modelData.detected_defects.forEach(defect => {
                    if (defect.type) {
                        defectSet.add(defect.type);
                    }
                });
            }
        });
    }

    // Filter out generic/non-defect terms that are classifications, not defect types
    const genericTerms = ['defective', 'good', 'normal', 'background'];
    const finalDefects = Array.from(defectSet).filter(
        type => !genericTerms.includes(type.toLowerCase())
    );

    return finalDefects;
};

export const getNumber = (field) => {
    if (field === null || field === undefined) return null;
    if (typeof field === 'number') return field;
    if (typeof field === 'object') return field.$numberDouble ?? field.$numberInt ?? field.$numberLong ?? null;
    return null;
};

export const getResultBadge = (inspection) => {
    const result = inspection.aggregate_result;
    if (!result) return <span className="badge bg-secondary">Unknown</span>;
    const classification = result.classification;
    return (
        <span className={`badge bg-${classification === 'defective' ? 'danger' : 'success'}`}>
            {classification}
        </span>
    );
};

export const getConfidence = (inspection, showConfidence = true) => {
    if (!showConfidence) return 'Hidden';

    const confidence = inspection.aggregate_result?.confidence;
    const num = getNumber(confidence);
    return num !== null ? `${(num * 100).toFixed(1)}%` : 'N/A';
};

export const getDefectTypes = (inspection) => {
    const allDefects = getAllDefectTypes(inspection);

    if (allDefects.length === 0) {
        // If the part is defective but no *specific* types were found, show a generic badge.
        if (inspection.aggregate_result?.classification === 'defective') {
            return <span className="badge bg-secondary">Defective</span>;
        }
        // Otherwise (if normal), show nothing.
        return <span className="text-muted">-</span>;
    }

    // Display all the specific, unique defect types found.
    return allDefects.map((defectType, i) => (
        <span key={i} className="badge bg-secondary me-1">{defectType}</span>
    ));
};

// ==================== EXPORT FUNCTIONS ====================

const exportToCSV = (data, filename = 'inspections_export.csv') => {
    if (!data || !Array.isArray(data) || data.length === 0) {
        throw new Error('No data available for export');
    }

    console.log('[ExportUtils] Exporting', data.length, 'inspections to CSV');

    // Define CSV headers
    const headers = [
        'Inspection ID',
        'Timestamp',
        'Part Name (Number)',
        'Station Name',
        'Station ID',
        'Lot ID',
        'Session ID',
        'Classification',
        'Confidence',
        'Defect Types',
        'Processing Time (ms)',
        'Ground Truth Status',
        'Confirmed By',
        'Confirmation Date',
    ];

    // Convert data to CSV rows
    const csvRows = data.map(inspection => {
        const defectTypes = getAllDefectTypes(inspection).join(', ');
        const confidence = inspection.aggregate_result?.confidence
            ? (getNumber(inspection.aggregate_result.confidence) * 100).toFixed(1) + '%'
            : 'N/A';

        return [
            inspection._id || '',
            inspection.inspection_time ? new Date(inspection.inspection_time).toISOString() : '',
            inspection.project_name || '',
            inspection.station_name || '',
            inspection.station_id || '',
            inspection.lot_identifier || '',
            inspection.session_id || 'Manual Mode',
            inspection.aggregate_result?.classification || '',
            confidence,
            defectTypes,
            inspection.processing_time_ms || '',
            inspection.confirmation_status || '',
            inspection.confirmed_by || '',
            inspection.confirmation_timestamp ? new Date(inspection.confirmation_timestamp).toISOString() : '',
        ].map(field => `"${String(field).replace(/"/g, '""')}"`); // Escape quotes
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...csvRows.map(row => row.join(','))].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log('[ExportUtils] CSV export completed:', filename);
};

const exportWithFallback = async (api, format, filters, fallbackData) => {
    try {
        console.log('[ExportUtils] Attempting server-side export');
        // Try server-side export first
        await api.exportInspectionData(format, filters);
        console.log('[ExportUtils] Server-side export successful');
    } catch (error) {
        console.warn('[ExportUtils] Server-side export failed, falling back to client-side:', error.message);

        // Fallback to client-side export
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `inspections_export_${timestamp}.${format}`;

        switch (format.toLowerCase()) {
            case 'csv':
                exportToCSV(fallbackData, filename);
                break;
            default:
                throw new Error(`Client-side export not supported for format: ${format}`);
        }

        console.log('[ExportUtils] Client-side export completed');
    }
};

// ==================== INSPECTION DETAILS MODAL ====================

export const InspectionDetailsModal = ({
    inspection,
    modelList,
    onClose,
    onImageClick,
    onSetGroundTruth,
    isLoading,
    addNotification
}) => {
    // FIXED: Get configuration from context properly
    const { state } = useAppContext();
    const show_confidence_scores = state.preferences?.show_confidence_scores ?? true;

    console.log('[InspectionDetailsModal] Configuration values:', {
        show_confidence_scores,
        preferences: state.preferences
    });

    // Export handler for individual inspection
    const handleExportInspection = useCallback(async (format) => {
        if (!addNotification) {
            console.warn('[InspectionDetailsModal] No notification function provided');
            return;
        }

        console.log(`[InspectionDetailsModal] Exporting inspection ${inspection._id} as ${format}`);
        addNotification({ type: 'info', message: `Preparing inspection ${format.toUpperCase()}...` });

        try {
            // For individual inspection export
            const filters = {
                inspection_id: inspection._id,
                dateRange: 'all'
            };

            await exportWithFallback(api, format, filters, [inspection]);

            addNotification({
                type: 'success',
                message: `Inspection ${format.toUpperCase()} downloaded successfully!`
            });
        } catch (error) {
            console.error('[InspectionDetailsModal] Export error:', error);
            addNotification({
                type: 'danger',
                message: `Failed to export inspection: ${error.message}`
            });
        }
    }, [inspection, addNotification]);

    const formatAggregateResult = (result, currentInspection) => {
        if (!result) return null;
        const confidence = getNumber(result.confidence);

        // Use the helper to get clean, specific defect types
        const displayedDefects = getAllDefectTypes(currentInspection);

        return (
            <div className="card mb-3 bg-light">
                <div className="card-header">
                    <h6 className="mb-0">Final Aggregate Result</h6>
                </div>
                <div className="card-body">
                    <p className="mb-1"><strong>Classification:</strong> {result.classification || 'N/A'}</p>
                    {/* CONDITIONAL: Only show confidence if enabled in configuration */}
                    {show_confidence_scores && (
                        <p className="mb-1">
                            <strong>Confidence:</strong> {
                                confidence !== null ? `${(confidence * 100).toFixed(1)}%` : 'N/A'
                            }
                        </p>
                    )}
                    {displayedDefects.length > 0 && (
                        <div>
                            <strong>Detected Defects:</strong>
                            {displayedDefects.map((type, i) => (
                                <span key={i} className="badge bg-secondary me-1 ms-1">{type}</span>
                            ))}
                        </div>
                    )}
                    {!show_confidence_scores && (
                        <small className="text-muted">
                            <i className="fas fa-info-circle me-1"></i>
                            Confidence scores hidden by configuration
                        </small>
                    )}
                </div>
            </div>
        );
    };

    // Fallback formatter for model names if the lookup fails
    const formatModelNameFallback = (name) => {
        if (!name) return "Unknown Model";
        return name
            .replace(/[-_]/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const individualModels = inspection.model_results?.models || inspection.model_results;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="fas fa-microscope me-2"></i>
                            Inspection Details: <code>{inspection._id}</code>
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {/* Configuration Status */}
                        <div className="alert alert-info">
                            <i className="fas fa-cog me-2"></i>
                            <small>
                                Configuration: Confidence scores {show_confidence_scores ? 'enabled' : 'disabled'}
                            </small>
                        </div>

                        <div className="row mb-4">
                            <div className="col-lg-6">
                                <h6>Inspection Details</h6>
                                <table className="table table-sm table-bordered">
                                    <tbody>
                                        <tr>
                                            <th>Timestamp</th>
                                            <td>{new Date(inspection.inspection_time).toLocaleString()}</td>
                                        </tr>
                                        <tr>
                                            <th>Part</th>
                                            <td>{inspection.project_name || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <th>Station</th>
                                            <td>{inspection.station_name || inspection.station_id || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <th>Lot ID</th>
                                            <td>{inspection.lot_identifier || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <th>Session ID</th>
                                            <td>
                                                {inspection.session_id ? (
                                                    <code>{inspection.session_id}</code>
                                                ) : (
                                                    <span className="text-muted">
                                                        <i className="fas fa-hand-paper me-1"></i>
                                                        Manual Mode (No Session)
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                        {inspection.processing_time_ms && (
                                            <tr>
                                                <th>Processing Time</th>
                                                <td>{inspection.processing_time_ms}ms</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                                <h6 className="mt-3">Ground Truth Classification</h6>
                                <GroundTruthControls
                                    inspection={inspection}
                                    onSetGroundTruth={onSetGroundTruth}
                                    isLoading={isLoading}
                                />
                            </div>
                            <div className="col-lg-6">
                                <h6>Original Image</h6>
                                <ImageWithFallback
                                    src={inspection.part_image_url}
                                    alt="Part Image"
                                    className="img-fluid rounded border"
                                    style={{
                                        maxHeight: '250px',
                                        width: '100%',
                                        objectFit: 'contain',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => onImageClick(inspection.part_image_url)}
                                />
                            </div>
                        </div>

                        <h6>Analysis Results</h6>
                        {formatAggregateResult(inspection.aggregate_result, inspection)}

                        <h6>Individual Model Results</h6>
                        <div className="row">
                            {individualModels && typeof individualModels === 'object' ? (
                                Object.entries(individualModels).map(([modelKey, modelData]) => {
                                    const modelInfo = modelList.find(m => m.key === modelKey);
                                    const displayName = modelInfo ? modelInfo.name : formatModelNameFallback(modelKey);

                                    return (
                                        <ModelVisualizationCard
                                            key={modelKey}
                                            displayName={displayName}
                                            modelData={modelData}
                                            onImageClick={onImageClick}
                                            showConfidence={show_confidence_scores} // Pass configuration
                                        />
                                    );
                                })
                            ) : (
                                <div className="col-12">
                                    <div className="alert alert-info">
                                        <i className="fas fa-info-circle me-2"></i>
                                        No individual model results available for this inspection.
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            <i className="fas fa-times me-2"></i>Close
                        </button>
                        {addNotification && (
                            <div className="btn-group">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => handleExportInspection('pdf')}
                                    disabled={isLoading}
                                >
                                    <i className="fas fa-file-pdf me-2"></i>Download PDF
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-primary dropdown-toggle dropdown-toggle-split"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                    disabled={isLoading}
                                >
                                    <span className="visually-hidden">Toggle Dropdown</span>
                                </button>
                                <ul className="dropdown-menu">
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleExportInspection('pdf')}
                                            disabled={isLoading}
                                        >
                                            <i className="fas fa-file-pdf me-2"></i>PDF Report
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleExportInspection('csv')}
                                            disabled={isLoading}
                                        >
                                            <i className="fas fa-file-csv me-2"></i>CSV Data
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==================== INSPECTION TABLE ROW COMPONENT ====================

export const InspectionTableRow = ({
    inspection,
    onViewDetails,
    onImageClick,
    showConfidence = true // Configuration parameter
}) => {
    return (
        <tr>
            <td>
                <ImageWithFallback
                    src={inspection.part_image_url}
                    alt="Part thumbnail"
                    className="rounded border"
                    style={{
                        width: '60px',
                        height: '40px',
                        objectFit: 'cover',
                        cursor: 'pointer'
                    }}
                    onClick={() => onImageClick(inspection.part_image_url)}
                />
            </td>
            <td>
                <small>{new Date(inspection.inspection_time).toLocaleString()}</small>
            </td>
            <td>{inspection.project_name || 'N/A'}</td>
            <td>{inspection.station_name || inspection.station_id || 'N/A'}</td>
            {/* CONDITIONAL: Only show confidence column if enabled */}
            {showConfidence && <td>{getConfidence(inspection, showConfidence)}</td>}
            <td>{getResultBadge(inspection)}</td>
            <td>{getDefectTypes(inspection)}</td>
            <td>
                <button
                    className="btn btn-sm btn-outline-primary"
                    title="View Details"
                    onClick={() => onViewDetails(inspection)}
                >
                    <i className="fas fa-eye"></i>
                </button>
            </td>
        </tr>
    );
};

// ==================== INSPECTION TABLE COMPONENT ====================

export const InspectionTable = ({
    inspections,
    onViewDetails,
    onImageClick,
    onSetGroundTruth,
    isLoading,
    filters = {},
    showExportButtons = true,
    title = "Inspections",
    addNotification
}) => {
    // FIXED: Get configuration from context properly
    const { state } = useAppContext();
    const show_confidence_scores = state.preferences?.show_confidence_scores ?? true;
    const includeManualImages = state.preferences?.includeManualImages ?? true;

    console.log('[InspectionTable] Configuration values:', {
        show_confidence_scores,
        includeManualImages,
        preferences: state.preferences
    });

    // Export handlers for the inspection table
    const handleExport = useCallback(async (format) => {
        if (!addNotification) {
            console.warn('[InspectionTable] No notification function provided');
            return;
        }

        console.log(`[InspectionTable] Exporting ${inspections.length} inspections as ${format}`);
        addNotification({ type: 'info', message: `Preparing ${format.toUpperCase()} export...` });

        try {
            // Use current data for export with fallback
            await exportWithFallback(api, format, filters, inspections);

            addNotification({
                type: 'success',
                message: `${format.toUpperCase()} export completed successfully!`
            });
        } catch (error) {
            console.error('[InspectionTable] Export error:', error);
            addNotification({
                type: 'danger',
                message: `Failed to export: ${error.message}`
            });
        }
    }, [inspections, filters, addNotification]);

    if (!inspections || inspections.length === 0) {
        return (
            <div className="card shadow-sm">
                <div className="card-header bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <i className="fas fa-microscope text-primary me-2"></i>
                            {title}
                        </h5>
                        {showExportButtons && (
                            <div className="btn-group">
                                <button className="btn btn-sm btn-outline-secondary" disabled>
                                    <i className="fas fa-file-csv me-1"></i>CSV
                                </button>
                                <button className="btn btn-sm btn-outline-secondary" disabled>
                                    <i className="fas fa-file-pdf me-1"></i>PDF
                                </button>
                                <button className="btn btn-sm btn-outline-secondary" disabled>
                                    <i className="fas fa-file-excel me-1"></i>Excel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <div className="card-body">
                    <div className="text-center p-5">
                        <i className="fas fa-inbox fa-4x text-muted mb-4"></i>
                        <h4 className="text-muted">No inspections found</h4>
                        <p className="text-muted">No inspections match the current filters.</p>
                        {includeManualImages === false && (
                            <p className="text-info">
                                <i className="fas fa-info-circle me-1"></i>
                                Manual images are excluded. Check Configuration tab to include them.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card shadow-sm">
            <div className="card-header bg-light">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h5 className="mb-0">
                            <i className="fas fa-microscope text-primary me-2"></i>
                            {title} ({inspections.length})
                        </h5>
                        {/* Configuration status */}
                        <small className="text-muted">
                            Confidence scores: {show_confidence_scores ? 'shown' : 'hidden'} •
                            Manual images: {includeManualImages ? 'included' : 'excluded'}
                        </small>
                    </div>
                    {showExportButtons && addNotification && (
                        <div className="btn-group" role="group">
                            <button
                                className="btn btn-sm btn-outline-info"
                                onClick={() => handleExport('csv')}
                                title="Export as CSV."
                                disabled={isLoading}
                            >
                                <i className="fas fa-file-excel me-1"></i>Download CSV
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <div className="card-body">
                <div className="table-responsive">
                    <table className="table table-hover table-sm align-middle">
                        <thead>
                            <tr>
                                <th>Image</th>
                                <th>Inspection Time</th>
                                <th>Part Name (Number)</th>
                                <th>Station Name</th>
                                {/* CONDITIONAL: Only show confidence column if enabled */}
                                {show_confidence_scores && <th>Confidence</th>}
                                <th>Result</th>
                                <th>Defect Types</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {inspections.map((inspection) => (
                                <InspectionTableRow
                                    key={inspection._id}
                                    inspection={inspection}
                                    onViewDetails={onViewDetails}
                                    onImageClick={onImageClick}
                                    showConfidence={show_confidence_scores} // Pass configuration
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};